// Friendly, human-readable names for SCA authentication methods.
//
// Banks return PSD2/Berlin-Group codes (e.g. PUSH_OTP) as `authenticationType`, plus an
// optional bank-specific `authenticationMethodId` (e.g. a BankID variant). We never want to
// show the raw code to the user. Labels are i18n-overridable: the remote translations can
// provide `scaMethod_<CODE>` keys; these English defaults are the fallback.
//
// The backend may also provide a `name` property for SCA methods, which should be preferred
// over the coded lookup. ScaApproach (DECOUPLED, REDIRECT) indicates authentication flow.
//
// Keys are normalised to UPPER_CASE; look-up tries authenticationMethodId first, then
// authenticationType, then falls back to the bank `name` or the raw value.
export const SCA_METHOD_LABELS = {
    // BankID method ids (matched by authenticationMethodId — checked FIRST). For BankID the
    // PSD2 authenticationType is misleading (e.g. CHIP_OTP = same-device BankID, not a card
    // reader), so these ids are the reliable signal. Values per docs.openpayments.io.
    MBID: 'Mobile BankID',
    MBID_SAME_DEVICE: 'BankID on this device',
    MBID_ANIMATED_QR_TOKEN: 'BankID on another device',
    MBID_ANIMATED_QR_IMAGE: 'BankID on another device',
    // Generic PSD2 / Berlin Group authentication types — fallback for non-BankID banks
    // (matched by authenticationType only when the method id is unknown).
    SMS_OTP: 'One-time code via SMS',
    CHIP_OTP: 'Card reader (security token)',
    PHOTO_OTP: 'QR code',
    PUSH_OTP: 'App push notification',
    SMTP_OTP: 'One-time code via email',
};

const normalize = (value) => (value ? String(value).trim().toUpperCase() : '');

// Check if a method contains a BankID variant. Normalise the ID; if both mbid and
// mbid_same_device are present with DECOUPLED, prefer mbid_same_device.
export const isBankIdMethod = (method) => {
    if (!method) return false;
    const id = normalize(method.authenticationMethodId);
    return id.includes('MBID');
};

// Resolve a display label for a scaMethod. `t` is the i18next translator so labels stay
// translatable (`t('scaMethod_PUSH_OTP', 'Mobilt BankID')`). Pass `(k, d) => d` to bypass i18n.
// Prefer the bank-provided `name` property if available.
export const getScaMethodLabel = (method, t = (key, def) => def) => {
    // Prefer the bank's own name if provided.
    if (method?.name) {
        return method.name;
    }

    const id = normalize(method?.authenticationMethodId);
    const type = normalize(method?.authenticationType);

    if (SCA_METHOD_LABELS[id]) {
        return t(`scaMethod_${id}`, SCA_METHOD_LABELS[id]);
    }
    if (SCA_METHOD_LABELS[type]) {
        return t(`scaMethod_${type}`, SCA_METHOD_LABELS[type]);
    }
    // Unknown code — show the raw value rather than nothing.
    return method?.authenticationType || method?.authenticationMethodId || '';
};
